/** Id used for products with no brand / no category, so they can still be filtered. */
export const NONE = "none";

export type FacetKey = "brand" | "category";

export type FacetOption = { id: string; label: string; count: number };

export type ProductFilters = {
  search: string;
  brands: string[];
  categories: string[];
  lowStock: boolean;
};

const FACETS: Record<FacetKey, { noneLabel: string; labelField: string; read: (item: any) => any }> = {
  brand: { noneLabel: "No brand", labelField: "brandName", read: (item) => item?.brandId },
  // The stored category; older products only reach it through the sub category.
  category: {
    noneLabel: "Uncategorised",
    labelField: "categoryName",
    read: (item) => (item?.categoryId?._id ? item.categoryId : item?.subCategoryId?.categoryId),
  },
};

function facetValue(item: any, key: FacetKey): { id: string; label: string } {
  const { noneLabel, labelField, read } = FACETS[key];
  const ref = read(item);
  if (!ref?._id) return { id: NONE, label: noneLabel };
  return { id: String(ref._id), label: ref[labelField] || "Unnamed" };
}

/** Every brand / category id present in the products (including NONE when some have none). */
export function presentIds(products: any[], key: FacetKey): Set<string> {
  return new Set(products.map((item) => facetValue(item, key).id));
}

export function isLowStock(item: any): boolean {
  return item?.availableQuantity <= item?.alertQuantity;
}

function matchesSearch(item: any, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return [item?.productName, item?.linkedDose?.drugName]
    .filter(Boolean)
    .some((text: string) => text.toLowerCase().includes(q));
}

function matches(item: any, filters: ProductFilters, skip?: FacetKey): boolean {
  if (!matchesSearch(item, filters.search)) return false;
  if (filters.lowStock && !isLowStock(item)) return false;
  if (skip !== "brand" && filters.brands.length && !filters.brands.includes(facetValue(item, "brand").id)) {
    return false;
  }
  if (
    skip !== "category" &&
    filters.categories.length &&
    !filters.categories.includes(facetValue(item, "category").id)
  ) {
    return false;
  }
  return true;
}

/** Ticks inside one filter widen the list (OR); different filters narrow it (AND). */
export function filterProducts(products: any[], filters: ProductFilters): any[] {
  return products.filter((item) => matches(item, filters));
}

/**
 * Every brand / category present in the products, A–Z with the "none" bucket last.
 * Counts are what ticking that option would add given the *other* active filters,
 * so they stay meaningful as you combine filters.
 */
export function facetOptions(products: any[], key: FacetKey, filters: ProductFilters): FacetOption[] {
  const byId = new Map<string, FacetOption>();
  for (const item of products) {
    const { id, label } = facetValue(item, key);
    if (!byId.has(id)) byId.set(id, { id, label, count: 0 });
    if (matches(item, filters, key)) byId.get(id)!.count += 1;
  }
  return Array.from(byId.values()).sort((a, b) => {
    if (a.id === NONE) return 1;
    if (b.id === NONE) return -1;
    return a.label.localeCompare(b.label);
  });
}
